/** @copyright (c) Viewpost. All Rights Reserved. See LICENSE for more details. */
/*
  Base class for components that intend to have only "configuration" children
  (children that will be consumed and manually processed by the parent instead of
  simply rendered)
*/

import { Children, Component } from 'react';
import PropTypes from 'prop-types';
import { isArray } from 'lodash';

const MAX_RECURSE_LEVELS = 10;

function findConfigurationChild(child, isConfigChild, recurseLevel = 0, context) {
  // support an array of child types
  if (isArray(child)) {
    if (child.every(item => isConfigChild(item.type, item.props))) {
      return child;
    }

    console.warn(`ComponentWithConfigurationChildren was given a child that is
        an array of config AND non-config children. This is not supported.`);
  }

  if (!child || !child.type) {
    // ignore null children
    return null;
  }

  if (isConfigChild(child.type, child.props)) {
    return child;
  }

  // since configuration children might be wrapped by other components,
  // recurse into children looking for them

  if (recurseLevel > MAX_RECURSE_LEVELS) {
    throw new Error(
      `ComponentWithConfigurationChildren reached maximum recursion limit
      when trying determine configuration children. Make sure all children eventually result in a configuration
      child matching its isConfigurationChild method check.`);
  }

  // Render the wrapper to find out what it produces. Class and function components have
  // to be invoked differently: a function component must be CALLED, not constructed, since
  // arrow functions are not constructors. (This previously used `new` for both, which only
  // worked because the IE 11 build target rewrote every arrow function into a function
  // declaration.)
  const isClassComponent = Boolean(child.type.prototype && child.type.prototype.isReactComponent);

  let childRendering;

  if (isClassComponent) {
    const instance = new child.type(child.props, context);

    // props/context are the 'this' for render() and are not always applied by the constructor
    instance.props = child.props;
    instance.context = context;

    childRendering = instance.render();
  } else {
    childRendering = child.type(child.props, context);
  }

  return findConfigurationChild(
    childRendering,
    isConfigChild,
    recurseLevel + 1
  );
}

export function getConfigurationChildren(children, isConfigChild, component) {
  let configChildren = [];
  Children.forEach(children, (child, index) => {
    let configChild = findConfigurationChild(child, isConfigChild, 0, component.context);
    if (isArray(configChild)) {
      // support arrays as children
      configChildren = configChildren.concat(configChild);
    } else if (configChild) {
      configChildren.push(configChild);
    }
  });
  return configChildren;
}

export function createGetConfigurationChildren(isConfigurationChild) {
  return function get() {
    return getConfigurationChildren(this.props.children, isConfigurationChild, this);
  };
}

// I would avoid using this in favor of using createGetConfigurationChildren, binding to
// getConfigurationChildren on your component
export default class ComponentWithConfigurationChildren extends Component {
  static contextTypes = {
    store: PropTypes.object
  };

  constructor(props) {
    super(props);
  }

  getConfigurationChildren() {
    if (!this.isConfigurationChild) {
      throw new Error(
        `ComponentWithConfigurationChildren (${this.constructor.name}) did not implement required method:
        Boolean isConfigurationChild(childType, childProps)`);
    }

    return getConfigurationChildren(this.props.children, this.isConfigurationChild.bind(this), this);
  }

}
